<template>
  <section class="memory-panel">
    <div class="header">
      <div><h3>长期记忆与人物关系</h3><p class="subtext">聊天中的明确个人信息可以被保存；游戏事件必须有真实执行记录，不能仅凭对话确认。</p></div>
      <el-button :loading="busy" @click="refresh">刷新</el-button>
    </div>
    <div class="selector">
      <span>查看谁的记忆</span>
      <el-select v-model="participant" style="width: min(100%, 310px)" @change="loadMemories">
        <el-option v-for="person in participants" :key="person.value" :label="person.label" :value="person.value" />
      </el-select>
      <el-button type="primary" plain @click="openCreate">添加记忆</el-button>
    </div>
    <p class="subtext">不同玩家的记忆和关系独立保存。当前 Web 试聊使用“我的 Web 对话”；Steam 玩家需等游戏身份接入后才能自动产生记录。</p>
    <div v-if="busy && !loaded" class="empty">正在读取记忆…</div>
    <div v-else-if="!memories.length" class="empty">暂时没有长期记忆。可以试着对 AI 说“请记住：我喜欢种草莓”，或者手动添加。</div>
    <div v-for="item in memories" :key="item.id" class="memory-card">
      <div class="memory-top">
        <span class="memory-category">{{ categories[item.category] || item.category }}</span>
        <el-tag size="small" effect="plain" :type="item.sourceKind==='VERIFIED_GAME_EVENT'?'success':'info'">{{ sources[item.sourceKind] || '来源未知' }}</el-tag>
      </div>
      <p class="memory-content">{{ item.content }}</p>
      <div class="memory-actions">
        <el-button text size="small" :disabled="item.sourceKind==='VERIFIED_GAME_EVENT'" @click="openEdit(item)">修改</el-button>
        <el-button text size="small" type="danger" @click="remove(item)">删除</el-button>
      </div>
    </div>
    <el-divider />
    <div class="header">
      <div><h3>与该玩家的关系备注</h3><p class="subtext">例如称呼、相处方式。不会自动增减游戏中的好感度。</p></div>
    </div>
    <el-form label-position="top" @submit.prevent="saveRelationship">
      <el-form-item label="玩家称呼"><el-input v-model="relationship.displayName" maxlength="80" show-word-limit placeholder="如：农场主人" /></el-form-item>
      <el-form-item label="关系说明"><el-input v-model="relationship.summary" type="textarea" :rows="3" maxlength="1200" show-word-limit placeholder="例如：我们经常讨论种田计划，但还没有一起实际完成任务" /></el-form-item>
      <el-button :loading="savingRelationship" @click="saveRelationship">保存关系备注</el-button>
    </el-form>
    <el-dialog v-model="editorVisible" :title="editingId?'修改记忆':'添加记忆'" width="min(96%, 490px)" append-to-body>
      <el-form label-position="top">
        <el-form-item label="记忆类型">
          <el-select v-model="editor.category" style="width:100%">
            <el-option v-for="option in categoryOptions" :key="option.value" :label="option.label" :value="option.value"/>
          </el-select>
        </el-form-item>
        <el-form-item label="记忆内容">
          <el-input v-model="editor.content" type="textarea" :rows="4" maxlength="600" show-word-limit placeholder="记录玩家喜好、个人背景或尚待兑现的约定" />
        </el-form-item>
        <p class="subtext">管理员添加的内容标记为“管理员备注”，不能伪装为已经执行的游戏事件。</p>
      </el-form>
      <template #footer>
        <el-button @click="editorVisible=false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveMemory">保存记忆</el-button>
      </template>
    </el-dialog>
  </section>
</template>

<script setup>
import {computed,onMounted,reactive,ref} from 'vue'
import {ElMessage,ElMessageBox} from 'element-plus'
import {http} from '../plugins/axios'
import {authErrorMessage} from '../auth'
const WEB='web:Quella'
const sources={PLAYER_SAID:'玩家自述',ADMIN:'管理员备注',VERIFIED_GAME_EVENT:'已验证游戏事件'}
const categories={PREFERENCE:'兴趣与喜好',BACKGROUND:'个人背景',COMMITMENT:'约定与目标',RELATIONSHIP:'相处与关系',EVENT:'经历'}
const categoryOptions=Object.entries(categories).map(([value,label])=>({value,label}))
const participant=ref(WEB),memories=ref([]),relations=ref([]),busy=ref(false),loaded=ref(false)
const editorVisible=ref(false),editingId=ref(null),saving=ref(false),savingRelationship=ref(false)
const editor=reactive({category:'PREFERENCE',content:''})
const relationship=reactive({displayName:'',summary:''})
const participants=computed(()=>{
  const values=[{value:WEB,label:'我的 Web 对话'}]
  for(const p of relations.value){
    if(p.participantKey && p.participantKey!==WEB){
      const label=p.displayName || (p.participantKey.startsWith('steam:')?'Steam 玩家':'其他玩家')
      values.push({value:p.participantKey,label:`${label}（${p.participantKey}）`})
    }
  }
  return values
})
function showError(error,label){ElMessage.error(authErrorMessage(error,label))}
function loadCurrentRelationship(){
  const found=relations.value.find(x=>x.participantKey===participant.value)
  relationship.displayName=found?.displayName||''
  relationship.summary=found?.summary||''
}
async function loadMemories(){
  busy.value=true
  try{
    const {data}=await http.get('/api/manage/ai/memories',{params:{participantKey:participant.value}})
    memories.value=Array.isArray(data)?data:[]
    loaded.value=true
    loadCurrentRelationship()
  }catch(e){showError(e,'记忆读取失败')}
  finally{busy.value=false}
}
async function refresh(){
  try{
    const {data}=await http.get('/api/manage/ai/relationships')
    relations.value=Array.isArray(data)?data:[]
  }catch(e){showError(e,'人物关系读取失败')}
  await loadMemories()
}
onMounted(refresh)
// eslint-disable-next-line no-undef
defineExpose({refresh})
function openCreate(){
  editingId.value=null
  editor.category='PREFERENCE';editor.content='';editorVisible.value=true
}
function openEdit(item){
  if(item.sourceKind==='VERIFIED_GAME_EVENT')return
  editingId.value=item.id
  editor.category=item.category;editor.content=item.content;editorVisible.value=true
}
async function saveMemory(){
  if(!editor.content.trim()||editor.content.trim().length<3){ElMessage.warning('记忆至少输入 3 个字');return}
  saving.value=true
  try{
    const body={category:editor.category,content:editor.content.trim()}
    if(editingId.value)await http.put(`/api/manage/ai/memories/${editingId.value}`,body)
    else await http.post('/api/manage/ai/memories',{...body,participantKey:participant.value})
    editorVisible.value=false
    ElMessage.success('记忆已保存')
    await loadMemories()
  }catch(e){showError(e,'保存记忆失败')}
  finally{saving.value=false}
}
async function remove(item){
  try{
    await ElMessageBox.confirm('删除后，AI 在后续对话中将不再引用这条长期记忆。确定删除吗？','删除记忆',{type:'warning',confirmButtonText:'删除',cancelButtonText:'取消'})
    await http.delete(`/api/manage/ai/memories/${item.id}`)
    ElMessage.success('记忆已删除')
    await loadMemories()
  }catch(e){if(e!=='cancel'&&e!=='close')showError(e,'删除失败')}
}
async function saveRelationship(){
  savingRelationship.value=true
  try{
    await http.put(`/api/manage/ai/relationships/${encodeURIComponent(participant.value)}`,{
      displayName:relationship.displayName,summary:relationship.summary
    })
    ElMessage.success('关系备注已保存')
    await refresh()
  }catch(e){showError(e,'保存关系备注失败')}
  finally{savingRelationship.value=false}
}
</script>

<style scoped>
.memory-panel{margin-top:20px;padding:27px;border:1px solid #e5ece7;border-radius:14px;background:#fff}
.header{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}
.header h3{font-size:17px;margin:0}.subtext{font-size:13px;color:#75847b;line-height:1.8;margin:6px 0 15px}
.selector{display:flex;align-items:center;flex-wrap:wrap;gap:12px;margin-top:18px}
.selector>span{font-size:13px;color:#4d6c58}.empty{background:#f7f9f2;border-radius:9px;padding:20px;color:#799080;font-size:13px;line-height:1.9}
.memory-card{margin-top:10px;border:1px solid #e6ece2;border-radius:10px;padding:13px 15px;background:#fcfdfa}
.memory-top{display:flex;align-items:center;gap:10px}.memory-category{font-size:13px;font-weight:600;color:#3d6b49}
.memory-content{font-size:14px;line-height:1.8;white-space:pre-wrap;overflow-wrap:anywhere;margin:8px 0}
.memory-actions{display:flex;gap:8px;justify-content:flex-end}
@media(max-width:560px){.memory-panel{padding:18px}.header{flex-wrap:wrap}.selector .el-select{width:100%!important}}
</style>
